// src/components/context/UserContext.js
import React, { createContext, useState, useEffect, useCallback } from 'react';
import axios from '../../config/axiosInstance';

export const UserContext = createContext();
const EMPTY_USER = { id: null, name: null, email: null, role: null };
const SESSION_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

export const UserProvider = ({ children }) => {
  const [user, setUser]               = useState(EMPTY_USER);
  const [permissions, setPermissions] = useState([]);
  const [sessionReady, setSessionReady] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authErrorCode, setAuthErrorCode] = useState(null);

  const clearSessionState = useCallback(() => {
    setUser(EMPTY_USER);
    setPermissions([]);
    setIsAuthenticated(false);
  }, []);

  const syncUserSession = useCallback(async () => {
    try {
      const response = await axios.get('api/auth/session');
      const data = response.data || {};

      if (!data.authenticated) {
        setAuthErrorCode(data.auth_error_code || null);
        clearSessionState();
        return false;
      }

      setUser({
        id: data.user_id ?? null,
        name: data.name ?? null,
        email: data.email ?? null,
        role: data.role ?? null,
      });
      setPermissions(data.permissions || []);
      setIsAuthenticated(true);
      setAuthErrorCode(null);
      return true;
    } catch (error) {
      console.error('No se pudo recuperar la sesión actual', error);

      const status = error?.response?.status ?? null;
      const isExplicitAuthFailure = status === 401 || status === 403;

      if (isExplicitAuthFailure || !isAuthenticated) {
        setAuthErrorCode(null);
        clearSessionState();
        return false;
      }

      // Si ya había sesión y el chequeo falla por un problema transitorio
      // de backend/red, preservamos la sesión actual para no desloguear al usuario.
      return true;
    } finally {
      setSessionReady(true);
    }
  }, [clearSessionState, isAuthenticated]);

  const logout = useCallback(async () => {
    try {
      await axios.post('api/auth/logout');
    } catch (error) {
      console.error('No se pudo cerrar la sesión en backend', error);
    } finally {
      localStorage.removeItem('authToken');
      setAuthErrorCode(null);
      clearSessionState();
      setSessionReady(true);
    }
  }, [clearSessionState]);

  useEffect(() => {
    syncUserSession();
  }, [syncUserSession]);

  useEffect(() => {
    if (!sessionReady) {
      return undefined;
    }

    const refreshSession = () => {
      syncUserSession();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshSession();
      }
    };

    window.addEventListener('focus', refreshSession);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        refreshSession();
      }
    }, SESSION_REFRESH_INTERVAL_MS);

    return () => {
      window.removeEventListener('focus', refreshSession);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.clearInterval(intervalId);
    };
  }, [sessionReady, syncUserSession]);

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        permissions,
        setPermissions,
        sessionReady,
        isAuthenticated,
        authErrorCode,
        syncUserSession,
        logout,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
